import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Clock, RotateCcw, GitBranch, CheckCircle2, X, GitCompare, User } from "lucide-react";
import { VisualDiffViewer } from "./VisualDiffViewer";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

export const VersionHistoryPanel = ({ workflowId, isOpen, onClose, onRevert }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [showRevertDialog, setShowRevertDialog] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [compareVersionA, setCompareVersionA] = useState(null);
  const [compareVersionB, setCompareVersionB] = useState(null);
  const [showDiffViewer, setShowDiffViewer] = useState(false);

  useEffect(() => {
    if (isOpen && workflowId) {
      fetchVersions();
    }
  }, [isOpen, workflowId]);

  const fetchVersions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/workflows/${workflowId}/commits`,
        { withCredentials: true }
      );
      setVersions(response.data.commits || []);
    } catch (error) {
      console.error("Failed to fetch versions:", error);
      toast.error("Failed to load version history");
    } finally {
      setLoading(false);
    }
  };

  const handleRevertClick = (version) => {
    setSelectedVersion(version);
    setShowRevertDialog(true);
  };

  const handleConfirmRevert = async () => {
    if (!selectedVersion) return;

    setReverting(true);
    try {
      const response = await axios.post(
        `${API}/workflows/${workflowId}/versions/${selectedVersion.version_id}/revert`,
        {},
        { withCredentials: true }
      );

      toast.success(`Reverted to version ${selectedVersion.version_number}`);
      setShowRevertDialog(false);
      
      // Refresh version list
      await fetchVersions();
      
      // Notify parent component to reload workflow
      if (onRevert) {
        onRevert(response.data.workflow);
      }
    } catch (error) {
      console.error("Failed to revert:", error);
      toast.error("Failed to revert to version");
    } finally {
      setReverting(false);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Version History
            </DialogTitle>
            <DialogDescription>
              View and restore previous versions of your workflow
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="h-[500px] pr-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : versions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <GitBranch className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No versions saved yet</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Save your workflow to create the first version
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {versions.map((version, index) => (
                  <div
                    key={version.version_id}
                    className="group relative p-4 rounded-xl border bg-card hover:bg-accent/50 transition-all"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant={index === 0 ? "default" : "outline"}>
                            Version {version.version_number}
                          </Badge>
                          {index === 0 && (
                            <Badge variant="secondary" className="bg-green-500/10 text-green-600 border-green-500/30">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Current
                            </Badge>
                          )}
                          {version.restored_from_version && (
                            <Badge variant="secondary" className="bg-blue-500/10 text-blue-600 border-blue-500/30">
                              <RotateCcw className="w-3 h-3 mr-1" />
                              Restored
                            </Badge>
                          )}
                        </div>

                        <p className="font-medium text-foreground mb-1">
                          {version.commit_message || `Version ${version.version_number}`}
                        </p>

                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          {version.commit_author && (
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {version.commit_author}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDistanceToNow(new Date(version.created_at), {
                              addSuffix: true,
                            })}
                          </span>
                          <span>
                            {version.nodes?.length || 0} nodes
                          </span>
                        </div>

                        {/* Diff Summary */}
                        {version.diff_summary && version.diff_summary.total_changes > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {version.diff_summary.nodes_added > 0 && (
                              <Badge variant="outline" className="text-xs bg-green-50 text-green-700">
                                +{version.diff_summary.nodes_added}
                              </Badge>
                            )}
                            {version.diff_summary.nodes_removed > 0 && (
                              <Badge variant="outline" className="text-xs bg-red-50 text-red-700">
                                -{version.diff_summary.nodes_removed}
                              </Badge>
                            )}
                            {version.diff_summary.nodes_modified > 0 && (
                              <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700">
                                ~{version.diff_summary.nodes_modified}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2 shrink-0">
                        {index !== 0 && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setCompareVersionA(versions[0].version_id);
                                setCompareVersionB(version.version_id);
                                setShowDiffViewer(true);
                              }}
                            >
                              <GitCompare className="w-4 h-4 mr-2" />
                              Compare
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRevertClick(version)}
                            >
                              <RotateCcw className="w-4 h-4 mr-2" />
                              Restore
                            </Button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Timeline connector */}
                    {index !== versions.length - 1 && (
                      <div className="absolute left-[18px] top-[70px] w-0.5 h-6 bg-border" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>
              <X className="w-4 h-4 mr-2" />
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revert Confirmation Dialog */}
      <AlertDialog open={showRevertDialog} onOpenChange={setShowRevertDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore this version?</AlertDialogTitle>
            <AlertDialogDescription>
              This will create a new version that is a copy of{" "}
              <strong>Version {selectedVersion?.version_number}</strong>. Your
              current work will be preserved in the version history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reverting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmRevert}
              disabled={reverting}
              className="bg-primary hover:bg-primary/90"
            >
              {reverting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Restoring...
                </>
              ) : (
                <>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Restore Version
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Visual Diff Viewer */}
      <VisualDiffViewer
        isOpen={showDiffViewer}
        onClose={() => setShowDiffViewer(false)}
        workflowId={workflowId}
        versionAId={compareVersionA}
        versionBId={compareVersionB}
      />
    </>
  );
};

export default VersionHistoryPanel;
